/**
 * ONE TAP — bootstrap.
 *
 * The whole client is assembled here and nowhere else: one renderer, one audio engine,
 * one input manager, one UI, one Game to drive them. Everything below is about the two
 * things a bootstrap actually owes the player.
 *
 *  1. **Never show a black screen.** A failure during boot — no WebGL, a bad bundle, a
 *     module that threw on import — must produce a sentence a human can act on. The
 *     failure surface is built from raw DOM with inline styles so it works even when the
 *     thing that broke *is* the UI module or the stylesheet.
 *  2. **Do not lie about being ready.** The `#boot` splash is removed only after the first
 *     frame has genuinely been presented, not when a promise resolved. Tearing the splash
 *     down early is how you get a flash of empty blue between "loading" and the game.
 */

import { Game } from '@client/game';
import { SettingsStore } from '@client/settings';
import { InputManager } from '@client/input/input';
import { Renderer } from '@client/render/renderer';
import { AudioEngine } from '@client/audio/engine';
import { UI, type UIHooks } from '@client/ui/ui';

/** How long the splash takes to fade once the first frame is up. */
const BOOT_FADE_MS = 260;
/** Non-fatal errors auto-dismiss; a fatal one stays until the page is reloaded. */
const TOAST_MS = 6000;

let booted = false;
let overlay: HTMLDivElement | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

// ---------------------------------------------------------------------------
// Failure surface
// ---------------------------------------------------------------------------

function describe(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return 'Something went wrong.';
}

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  const el = document.createElement('div');
  el.id = 'onetap-fault';
  el.setAttribute('role', 'alert');
  el.style.cssText = [
    'position:fixed',
    'z-index:1000',
    'inset:auto 0 0 0',
    'padding:1rem 1.25rem',
    'font:400 0.78rem/1.5 ui-monospace,SFMono-Regular,Menlo,monospace',
    'letter-spacing:0.04em',
    // Sunlit Cel: frosted glass, slate text, semantics carried by the accent
    // colour of the message itself rather than a decorative sidebar/edge bar.
    'color:#2a3242',
    'background:rgba(255,255,255,0.92)',
    'backdrop-filter:saturate(180%) blur(14px)',
    'border-top:1px solid rgba(42,50,66,0.12)',
    'box-shadow:0 -6px 24px rgba(42,50,66,0.10)',
    'white-space:pre-wrap',
  ].join(';');
  document.body.appendChild(el);
  overlay = el;
  return el;
}

/** Unrecoverable: fill the screen, explain, and offer the one action that can help. */
function fatal(message: string, detail?: unknown): void {
  const el = ensureOverlay();
  if (toastTimer !== null) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
  el.style.inset = '0';
  el.style.display = 'grid';
  el.style.placeContent = 'center';
  el.style.textAlign = 'center';
  el.style.borderTop = 'none';
  el.textContent = '';

  // Sunlit Cel (DESIGN.md §4): light type on white, soft slate ink, one saturated accent.
  // No offset colour shadows, no true black — this screen is the first thing a player with
  // a broken machine ever sees and it still has to look like the game they were promised.
  const title = document.createElement('div');
  title.textContent = 'ONE TAP COULD NOT START';
  title.style.cssText =
    "font:200 clamp(1.4rem,4vw,2.2rem)/1 'Helvetica Neue',Inter,system-ui,-apple-system,Helvetica,Arial,sans-serif;letter-spacing:0.1em;margin-bottom:1rem;color:#2a3242";

  const body = document.createElement('div');
  body.textContent = message;
  body.style.cssText = 'max-width:44rem;margin:0 auto 1.25rem;color:#6b7688';

  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'RELOAD';
  reload.style.cssText =
    'justify-self:center;appearance:none;cursor:pointer;padding:0.7rem 1.6rem;font:inherit;letter-spacing:0.2em;color:#fafaf8;background:#0a84ff;border:1px solid #0a84ff;border-radius:12px';
  reload.addEventListener('click', () => location.reload());

  // `appendChild` rather than `append`: the project pulls in @cloudflare/workers-types,
  // whose HTMLRewriter `Element.append` shadows the DOM signature.
  el.appendChild(title);
  el.appendChild(body);
  el.appendChild(reload);
  removeBoot(true);

  if (detail !== undefined) console.error('[onetap] fatal:', detail);
}

/** Recoverable: a strip along the bottom that says what happened, then gets out of the way. */
function toast(message: string, detail?: unknown): void {
  const el = ensureOverlay();
  if (el.style.inset === '0px' || el.style.inset === '0') return; // a fatal screen owns it
  el.textContent = message;
  el.style.display = 'block';
  if (toastTimer !== null) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastTimer = null;
    if (overlay) overlay.style.display = 'none';
  }, TOAST_MS);
  if (detail !== undefined) console.error('[onetap]', detail);
}

function report(err: unknown, context: string): void {
  const message = `${context}: ${describe(err)}`;
  if (!booted) fatal(message, err);
  else toast(message, err);
}

// ---------------------------------------------------------------------------
// Boot splash
// ---------------------------------------------------------------------------

function removeBoot(immediate = false): void {
  const el = document.getElementById('boot');
  if (!el) return;
  if (immediate) {
    el.remove();
    return;
  }
  el.style.transition = `opacity ${BOOT_FADE_MS}ms ease`;
  el.style.opacity = '0';
  el.style.pointerEvents = 'none';
  setTimeout(() => el.remove(), BOOT_FADE_MS);
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

function webglAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function boot(): void {
  const container = document.getElementById('app');
  if (!container) {
    fatal('The page is missing its #app container. Try a hard reload.');
    return;
  }

  if (!webglAvailable()) {
    fatal(
      'This browser cannot open a WebGL context.\n\n' +
        'ONE TAP needs hardware-accelerated graphics. Check that hardware acceleration is\n' +
        'enabled in your browser settings, then reload.',
    );
    return;
  }

  const settings = new SettingsStore();

  let renderer: Renderer;
  try {
    renderer = new Renderer(container);
  } catch (err) {
    fatal(`The renderer could not start: ${describe(err)}`, err);
    return;
  }

  const audio = new AudioEngine();
  const input = new InputManager(renderer.gl.domElement);

  // The UI is built before the Game so it can be handed in, but its hooks have to reach a
  // Game that does not exist yet — hence the late-bound closure rather than a direct
  // reference. Every hook is a no-op until `game` is assigned, which is exactly the
  // correct behaviour for a click that lands during construction.
  let game: Game | null = null;
  const hooks: UIHooks = {
    onCreateRoom: (name) => void game?.createRoom(name),
    onJoinRoom: (code, name) => void game?.joinRoom(code, name),
    onReady: (r) => game?.setReady(r),
    onSwitchTeam: (t) => game?.switchTeam(t),
    onStartMatch: () => game?.startMatch(),
    onSetBots: (f) => game?.setBots(f),
    onLeave: () => game?.leave(),
    onSensitivity: (v) => game?.setSensitivity(v),
    onVolume: (v) => game?.setVolume(v),
  };

  const ui = new UI(container, hooks);

  // The UI owns the sliders for sensitivity and volume and persists them under its own
  // storage key. Seed this store from those values so the engine and the sliders agree
  // from the very first frame instead of only after the player touches one.
  const uiSettings = ui.currentSettings();
  settings.patch({
    sensitivity: uiSettings.sensitivity,
    volume: uiSettings.masterVolume,
    playerName: uiSettings.name || settings.get().playerName,
  });

  game = new Game({ container, renderer, ui, audio, input, settings });
  game.onFirstFrame = () => removeBoot();
  game.onFatal = (message, detail) => fatal(message, detail);

  // Push persisted settings into the live subsystems, and keep doing so on every change.
  settings.attach({ renderer, audio, input });

  // A full teardown on unload is not strictly required — the tab is going away — but it
  // keeps the dev server's hot reloads from stacking WebGL contexts and listeners.
  window.addEventListener('pagehide', () => {
    settings.flush();
    game?.dispose();
    input.dispose();
    audio.dispose();
  });

  booted = true;
  game.start();
}

// ---------------------------------------------------------------------------

window.addEventListener('error', (e: ErrorEvent) => {
  report(e.error ?? e.message, 'Unexpected error');
});

window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
  report(e.reason, 'Unhandled failure');
});

try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      try {
        boot();
      } catch (err) {
        fatal(`Startup failed: ${describe(err)}`, err);
      }
    });
  } else {
    boot();
  }
} catch (err) {
  fatal(`Startup failed: ${describe(err)}`, err);
}
